import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import ReactTooltip from 'react-bootstrap/Tooltip';
import moment from 'moment';
import { Dropdown } from 'react-bootstrap';
import classNames from 'classnames';
import { user } from '../../../redux/user';
import Permissions from '../../../constants/Permissions';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import IsAuthorizedForManagementCompany from '../../../components/IsAuthorizedForManagementCompany/IsAuthorizedForManagementCompany';
import {
	useGetNotesQuery,
	useAddNoteMutation,
	useDeleteNoteMutation,
	useGetNoteCategoriesQuery,
	useUpdateNoteMutation
} from '../../../redux/managementCompanyWorkforce';
import Select from '../../../components/form/Select';
import MultiSelectSearch from '../../../components/MultiSelectSearch/MultiSelectSearch';
import { showNotification } from '../../../redux/notification';
import KebobIcon from '../../../assets/images/more-horizontal.svg';
import utils from '../../../utils/utils';
import ConfirmationModal from '../../../components/modals/ConfirmationModalNew';
import searchIcon from '../../../assets/images/search-icon.svg';
import closeIcon from '../../../assets/images/close-black.svg';
import searchNoResultsIcon from '../../../assets/images/search-no-results.svg';

const FlexProfessionalNotes = ({ managementCompanyId, caregiverId, caregiverName, onDirtyChange }) => {
	const dispatch = useDispatch();
	const userData = useSelector(user);

	const [addFormActive, setAddFormActive] = useState(false);
	const [newNotes, setNewNotes] = useState('');
	const [noteToDelete, setNoteToDelete] = useState(null);
	const [selectedCategory, setSelectedCategory] = useState(null);
	const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] = useState(false);
	const [noteToEdit, setNoteToEdit] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');
	const [categoryOptions, setCategoryOptions] = useState([]);
	const [authorOptions, setAuthorOptions] = useState([]);

	const hasDeleteSubmittedPermission = utils.isAuthorizedExact(
		Permissions.WORKFORCE_NOTES_DELETE_SUBMITTED,
		userData.managementCompanyPermissions[managementCompanyId]
	);

	const hasDeleteAllPermission = utils.isAuthorizedExact(
		Permissions.WORKFORCE_NOTES_DELETE_ALL,
		userData.managementCompanyPermissions[managementCompanyId]
	);

	const hasEditPermission = utils.isAuthorizedExact(
		Permissions.WORKFORCE_NOTES_EDIT,
		userData.managementCompanyPermissions[managementCompanyId]
	);

	const { isLoading: isLoadingCategories, data: categories } = useGetNoteCategoriesQuery(
		{ managementCompanyId },
		{ refetchOnMountOrArgChange: true }
	);
	const { isLoading: isLoadingNotes, data: notes } = useGetNotesQuery(
		{ managementCompanyId, caregiverId },
		{ refetchOnMountOrArgChange: true }
	);

	useEffect(() => {
		setCategoryOptions(
			(categories || []).map((category) => ({ label: category.label, value: category.value, checked: true }))
		);
	}, [categories]);

	useEffect(() => {
		const authors = [];
		const seenUserIds = new Set();

		// we build the list of author options from the notes that have a non-contact removed author
		// and add (contact removed) at the end of the list if there are such notes
		(notes || [])
			.filter((note) => !!note.added_by)
			.forEach((note) => {
				if (seenUserIds.has(note.portal_user_id)) {
					return;
				}
				seenUserIds.add(note.portal_user_id);

				let labelSuffix = null;

				if (note.is_current_user) {
					labelSuffix = '(you)';
				} else if (note.is_inactive) {
					labelSuffix = '(deactivated)';
				}

				authors.push({
					label: note.added_by ?? '',
					value: note.portal_user_id,
					labelSuffix,
					labelSuffixClass: labelSuffix ? 'grayed-out-label' : null,
					checked: true
				});
			});

		authors.sort((option1, option2) => option1.label.localeCompare(option2.label));

		const contactRemovedNote = notes?.find((note) => !note.added_by);
		if (contactRemovedNote) {
			authors.push({
				label: '',
				value: null,
				labelSuffix: '(contact removed)',
				labelSuffixClass: 'grayed-out-label',
				checked: true
			});
		}

		setAuthorOptions((oldAuthors) => {
			for (let i = 0; i < authors.length; i++) {
				const a = authors[i];
				const existingOption = oldAuthors.find((oa) => oa.value === a.value);
				if (existingOption) {
					authors[i] = { ...existingOption };
				}
			}
			return authors;
		});
	}, [notes]);

	const handleCategoryFilterApply = (options) => setCategoryOptions(options);

	const handleAuthorFilterApply = (options) => setAuthorOptions(options);

	const handleClearAll = () => {
		setSearchTerm('');
		setCategoryOptions((options) => options.map((option) => ({ ...option, checked: true })));
		setAuthorOptions((options) => options.map((option) => ({ ...option, checked: true })));
	};

	const filteredNotes = useMemo(() => {
		if (!notes) {
			return [];
		}
		const normalizedSearch = searchTerm.trim().toLowerCase();
		const isCategoryFilterActive = categoryOptions.some((option) => !option.checked);
		const isAuthorFilterActive = authorOptions.some((option) => !option.checked);
		const checkedCategoryValues = categoryOptions.filter((option) => option.checked).map((option) => option.value);
		const checkedAuthorValues = authorOptions.filter((option) => option.checked).map((option) => option.value);
		return notes.filter((note) => {
			const matchesCategory = !isCategoryFilterActive || checkedCategoryValues.includes(note.category_code);
			// if we filter by (contact removed), we look at note.added_by because we can have multiple portal users displayed as (contact removed)
			const contactRemovedOptionSelected = checkedAuthorValues.includes(null) && !note.added_by;
			const matchesAuthor =
				!isAuthorFilterActive || checkedAuthorValues.includes(note.portal_user_id) || contactRemovedOptionSelected;
			const matchesSearch =
				!normalizedSearch ||
				[note.content, note.formatted_creation_date, note.formatted_last_updated_date].some((field) =>
					(field ?? '').toLowerCase().includes(normalizedSearch)
				);
			return matchesCategory && matchesAuthor && matchesSearch;
		});
	}, [notes, categoryOptions, authorOptions, searchTerm]);

	const [addNotes, { isLoading: loadingUpdate }] = useAddNoteMutation();
	const [deleteNote] = useDeleteNoteMutation();
	const [updateNote, { isLoading: isUpdateInProgress }] = useUpdateNoteMutation();

	const handleSelectCategory = ({ category }) => setSelectedCategory(categories.find((cat) => cat?.value === category));

	useEffect(() => {
		if (onDirtyChange) {
			onDirtyChange(!!newNotes || !!selectedCategory || !!(noteToEdit && noteToEdit.isDirty));
		}
	}, [onDirtyChange, newNotes, selectedCategory, noteToEdit]);

	const handleCancel = () => {
		setAddFormActive(false);
		setNewNotes('');
		setSelectedCategory(null);
		if (onDirtyChange) {
			onDirtyChange(false);
		}
	};

	const performDelete = async () => {
		try {
			await deleteNote({
				managementCompanyId,
				caregiverId,
				noteId: noteToDelete.id
			}).unwrap();
			dispatch(showNotification('success', `The note for ${caregiverName} has been deleted.`));
		} catch (err) {
			dispatch(showNotification('error', `There was an issue deleting the selected note. Please try again.`));
		} finally {
			setShowDeleteConfirmationModal(false);
		}
	};

	const handleDelete = (note) => {
		setNoteToDelete(note);
		setShowDeleteConfirmationModal(true);
	};

	const showDeleteOption = (isCurrentUser) => (isCurrentUser && hasDeleteSubmittedPermission) || hasDeleteAllPermission;

	const showEditOption = (isCurrentUser) => isCurrentUser && hasEditPermission;

	const showActionMenu = (isCurrentUser) => showEditOption(isCurrentUser) || showDeleteOption(isCurrentUser);

	const handlePublish = async () => {
		try {
			await addNotes({
				managementCompanyId,
				caregiverId,
				body: {
					note: newNotes.trim(),
					category: selectedCategory.value
				}
			}).unwrap();
			dispatch(showNotification('success', `Your note for ${caregiverName} has been published.`));
			handleCancel();
		} catch (err) {
			dispatch(showNotification('error', `There was an issue completing this action. Please try again.`));
		}
	};

	const handleEdit = async () => {
		if (!noteToEdit) {
			return;
		}

		try {
			await updateNote({
				managementCompanyId,
				caregiverId,
				noteId: noteToEdit.id,
				body: {
					note: noteToEdit.content,
					category: noteToEdit.category_code,
					lastUpdatedDate: noteToEdit.last_updated_date
				}
			}).unwrap();
			dispatch(showNotification('success', `Your note for ${caregiverName} has been updated.`));
			setNoteToEdit(null);
		} catch (err) {
			let message = `There was an issue completing this action. Please try again.`;
			if (err?.data?.validationError) {
				message = err.data.errors?.[0]?.message ?? message;
			}
			dispatch(showNotification('error', message));
		}
	};

	const hasCategoriesUnchecked = categoryOptions.some((opt) => !opt.checked);
	const hasAuthorsUnchecked = authorOptions.some((opt) => !opt.checked);
	const areFiltersDirty = hasCategoriesUnchecked || hasAuthorsUnchecked || !!searchTerm;

	if (isLoadingCategories) return <LoadingSpinner />;

	return [
		<WithOnlyOnDesktop key="notes-section">
			<div className="flex-professional-notes">
				<div className="header-row">
					<div className="title">Notes</div>
					{!noteToEdit && !addFormActive && (
						<IsAuthorizedForManagementCompany
							exactPermission={Permissions.WORKFORCE_NOTES_EDIT}
							managementCompanyId={managementCompanyId}
						>
							{categories?.length ? (
								<Button className="btn add-note-button" onClick={() => setAddFormActive(true)}>
									+ Add note
								</Button>
							) : (
								<OverlayTrigger
									className="tooltip-component"
									placement="left"
									delay={{ show: 100, hide: 400 }}
									overlay={
										<ReactTooltip className="add-note-tooltip" id="info-tooltip" scrollHide={false} resizeHide={false}>
											<div className="tooltip-content">
												You cannot add a note as you do not have access to any note categories.
											</div>
										</ReactTooltip>
									}
								>
									<Button className="btn add-note-button-disabled" onClick={() => {}}>
										+ Add note
									</Button>
								</OverlayTrigger>
							)}
						</IsAuthorizedForManagementCompany>
					)}
				</div>
				{!noteToEdit && addFormActive && (
					<div className="add-note-form-container">
						<div className="notes-labels">
							<div>Add note</div>
							<div>
								({newNotes.length}/{userData.workforceNotesMaxChars})
							</div>
						</div>
						<textarea
							id="add-note"
							className="notes-textarea"
							maxLength={userData.workforceNotesMaxChars}
							value={newNotes}
							onChange={(e) => setNewNotes(e.target.value)}
						/>
						<div className="notes-footer">
							<Select
								className="category-select"
								handleChange={handleSelectCategory}
								name="category"
								options={categories}
								placeholder="Select a category"
								value={selectedCategory}
								disabled={isLoadingCategories}
								isSearchable={false}
							/>
							<div className="d-flex">
								<button type="button" onClick={handleCancel} className="btn-cancel">
									Cancel
								</button>
								<button
									type="button"
									disabled={
										!selectedCategory || newNotes.trim().length < userData.workforceNotesMinChars || loadingUpdate
									}
									className="btn publish-button"
									onClick={handlePublish}
								>
									Publish note
								</button>
							</div>
						</div>
					</div>
				)}
				{notes?.length > 0 && (
					<div className="notes-filters">
						<MultiSelectSearch
							showLongSelectionText
							className="notes-filter-dropdown"
							label="Category"
							options={categoryOptions}
							onApply={handleCategoryFilterApply}
							disabled={!!noteToEdit}
						/>
						<MultiSelectSearch
							showLongSelectionText
							className="notes-filter-dropdown"
							label="Author"
							options={authorOptions}
							onApply={handleAuthorFilterApply}
							disabled={!!noteToEdit}
						/>
						<div className="search-container">
							<img src={searchIcon} alt="search" className="search-icon" />
							<input
								type="text"
								className="search-input"
								placeholder="Search for..."
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								disabled={!!noteToEdit}
							/>
							{!!searchTerm.length && (
								 
								<img src={closeIcon} alt="delete" className="delete-icon" onClick={() => setSearchTerm('')} />
							)}
						</div>
						{areFiltersDirty && (
							<button
								type="button"
								className="clear-all-button filter-section-button"
								onClick={handleClearAll}
								disabled={!!noteToEdit}
							>
								Clear all
							</button>
						)}
					</div>
				)}
				{!isLoadingNotes && filteredNotes.length > 0 && categories?.length > 0 && (
					<div className="notes-container">
						{filteredNotes.map((note) => {
							const isEditMode = note.id === noteToEdit?.id;

							return (
								<div className="note" key={note.id}>
									<div className="note-header">
										{!isEditMode && <div className="category">{note.category}</div>}
										<div className="d-flex">
											<div className="date">{note.formatted_creation_date}</div>
											<div className="right-header-section-wrapper">
												<div className="author">
													{note.added_by && (
														<>
															{!note.is_inactive && note.added_by}
															{note.is_current_user && <span className="current-user"> (you)</span>}
															{note.is_inactive && <span className="current-user">{note.added_by} (deactivated)</span>}
														</>
													)}
													{!note.added_by && <span className="current-user">(contact removed)</span>}
												</div>
												{!isEditMode && showActionMenu(note.is_current_user) ? (
													<Dropdown>
														<Dropdown.Toggle
															className={classNames('action-menu', {
																disabled: noteToEdit !== null || addFormActive === true
															})}
														>
															<img src={KebobIcon} alt="Show More" className="action-menu-icon" />
														</Dropdown.Toggle>
														<Dropdown.Menu>
															{showEditOption(note.is_current_user) && (
																<Dropdown.Item onClick={() => setNoteToEdit(note)}>Edit</Dropdown.Item>
															)}
															{showDeleteOption(note.is_current_user) && (
																<Dropdown.Item className="action-menu-delete" onClick={() => handleDelete(note)}>
																	Delete
																</Dropdown.Item>
															)}
														</Dropdown.Menu>
													</Dropdown>
												) : (
													<div className="kebab-placeholder" />
												)}
											</div>
										</div>
									</div>
									{!isEditMode ? (
										<div className="note-content">
											{note.content}
											{note.last_updated_date && <em> (Last edited on {note.formatted_last_updated_date})</em>}
										</div>
									) : (
										<div
											className={classNames('add-note-form-container', {
												'note-too-long': noteToEdit.content.length > userData.workforceNotesMaxChars
											})}
										>
											<div className="notes-labels">
												<div>Edit note</div>
												<div className="notes-char-counter">
													({noteToEdit.content.length}/{userData.workforceNotesMaxChars})
												</div>
											</div>
											<textarea
												id="edit-note"
												className="notes-textarea"
												value={noteToEdit.content}
												maxLength={
													noteToEdit.content.length <= userData.workforceNotesMaxChars
														? userData.workforceNotesMaxChars
														: noteToEdit.content.length
												}
												onChange={(e) => setNoteToEdit({ ...noteToEdit, content: e.target.value, isDirty: true })}
											/>
											<div className="notes-footer">
												<Select
													className="category-select"
													handleChange={({ category }) =>
														setNoteToEdit({ ...noteToEdit, category_code: category, isDirty: true })
													}
													name="category"
													options={categories}
													placeholder="Select a category"
													value={noteToEdit.category_code}
													disabled={isLoadingCategories}
													isSearchable={false}
												/>
												<div className="d-flex">
													<button
														type="button"
														onClick={() => setNoteToEdit(null)}
														className="btn-cancel"
														disabled={isUpdateInProgress}
													>
														Cancel
													</button>
													<button
														type="button"
														disabled={
															!noteToEdit.category_code ||
															noteToEdit.content.length < userData.workforceNotesMinChars ||
															noteToEdit.content.length > userData.workforceNotesMaxChars ||
															isUpdateInProgress ||
															(noteToEdit.category_code === note.category_code && noteToEdit.content === note.content)
														}
														className="btn publish-button"
														onClick={handleEdit}
													>
														Update note
													</button>
												</div>
											</div>
										</div>
									)}
								</div>
							);
						})}
					</div>
				)}
				{!categories?.length && <div className="no-notes">You do not have permission to view any note categories.</div>}
				{categories?.length > 0 && !isLoadingNotes && !notes?.length && (
					<div className="no-notes">There are no notes for this professional.</div>
				)}
				{categories?.length > 0 && !isLoadingNotes && notes?.length > 0 && filteredNotes.length === 0 && (
					<div className="no-notes-match">
						<img src={searchNoResultsIcon} alt="The are no notes that match your filters or search" />
						<div className="no-notes-match-message">There are no notes that match your filters or search.</div>
						<button type="button" className="clear-all-button" onClick={handleClearAll}>
							Clear all
						</button>
					</div>
				)}
			</div>
		</WithOnlyOnDesktop>,
		showDeleteConfirmationModal && (
			<ConfirmationModal
				key="delete-confirmation-modal"
				title="Delete Note?"
				size="lg"
				onConfirm={performDelete}
				onCancel={() => setShowDeleteConfirmationModal(false)}
				confirmLabel="Delete note"
				cancelLabel="Keep note"
				disableBackdrop
			>
				<div>
					<p>
						This will permanently delete this note and cannot be undone. Please ensure that you have selected the
						correct note before continuing.
					</p>

					<div className="delete-modal-note-section">
						<div className="delete-modal-note" key={`${noteToDelete.id}-to-delete`}>
							<div className="note-header">
								<div className="d-flex">
									<div className="left-section">
										<div className="date">{moment(noteToDelete.creation_date).format('MM/DD/YYYY - h:mm A')}</div>
										<div className="category">{noteToDelete.category}</div>
									</div>

									<div className="right-header-section-wrapper">
										<div className="author">
											{noteToDelete.added_by && (
												<>
													{!noteToDelete.is_inactive && noteToDelete.added_by}
													{noteToDelete.is_current_user && <span className="current-user"> (you)</span>}
													{noteToDelete.is_inactive && (
														<span className="current-user">{noteToDelete.added_by} (deactivated)</span>
													)}
												</>
											)}
											{!noteToDelete.added_by && <span className="current-user">(contact removed)</span>}
										</div>
									</div>
								</div>
							</div>
							<div className="note-content">
								{noteToDelete.content}
								{noteToDelete.last_updated_date && (
									<em> (Last edited on {moment(noteToDelete.last_updated_date).format('MM/DD/YYYY - h:mm A')})</em>
								)}
							</div>
						</div>
					</div>
				</div>
			</ConfirmationModal>
		)
	];
};

export default FlexProfessionalNotes;
